import { useState, useRef } from 'react'
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { useAuthStore } from '@/store/auth'
import { apiPost, API_BASE } from '@/lib/api'
import { AuthResponseSchema } from '@ekorank/shared'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { z } from 'zod'

const ApiAuthResponse = z.object({ ok: z.literal(true), data: AuthResponseSchema })

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
      </svg>
    )
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
}

function GitHubIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
    </svg>
  )
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setAuth = useAuthStore((s) => s.setAuth)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated())

  if (isAuthenticated) return <Navigate to="/dashboard" replace />

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(
    searchParams.get('error') === 'oauth_failed' ? 'Social login failed. Please try again.' : null
  )
  const [turnstileToken, setTurnstileToken] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const turnstileRef = useRef<TurnstileInstance>(null)

  const emailError = emailTouched && email && !isValidEmail(email) ? 'Invalid email address' : null
  const passwordError = passwordTouched && password && password.length < 8 ? 'Minimum 8 characters' : null
  const canSubmit = !loading && isValidEmail(email) && password.length >= 8 && !!turnstileToken

  const handleLogin = async () => {
    if (!canSubmit) return
    setLoading(true)
    setError(null)
    const result = await apiPost('/api/auth/login', ApiAuthResponse, { email, password, turnstileToken })
    setLoading(false)
    if (!result.ok) {
      setError(result.error)
      setTurnstileToken('')
      turnstileRef.current?.reset()
      return
    }
    setAuth(result.data.data.user, result.data.data.token)
    navigate('/dashboard')
  }

  return (
    <div
      className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12"
      style={{ backgroundImage: 'radial-gradient(circle, rgb(255 255 255 / 0.035) 1px, transparent 1px)', backgroundSize: '24px 24px' }}
    >
      <div className="flex items-center gap-2 mb-10">
        <span className="text-primary text-2xl font-bold">▐</span>
        <span className="text-xl font-bold tracking-tighter text-white">Ekorank</span>
      </div>

      <div className="w-full max-w-md bg-surface-container border border-outline-variant/20 rounded-xl shadow-2xl overflow-hidden">
        <div className="px-8 pt-8 pb-2">
          <h1 className="text-2xl font-bold text-white">Welcome back</h1>
          <p className="mt-1 text-sm text-on-surface-variant">Sign in to your Ekorank account</p>
        </div>

        <div className="px-8 py-6 space-y-5">
          {error && (
            <div className="p-3 rounded-lg bg-error/10 border border-error/30 text-error text-sm">
              {error}
            </div>
          )}

          {/* Social login */}
          <div className="grid grid-cols-2 gap-3">
            <a
              href={`${API_BASE}/api/auth/github`}
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-outline-variant/30 bg-surface-container-low text-white text-sm hover:bg-surface-variant transition-colors"
            >
              <GitHubIcon /> GitHub
            </a>
            <a
              href={`${API_BASE}/api/auth/google`}
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-outline-variant/30 bg-surface-container-low text-white text-sm hover:bg-surface-variant transition-colors"
            >
              <GoogleIcon /> Google
            </a>
          </div>

          {/* Divider */}
          <div className="relative flex items-center gap-3">
            <div className="flex-1 h-px bg-outline-variant/20" />
            <span className="text-[11px] text-outline uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-outline-variant/20" />
          </div>

          {/* Email */}
          <div>
            <label className="block font-mono text-[10px] text-outline uppercase tracking-widest mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLogin() }}
              className={`w-full bg-surface-container-low border-b text-white p-3 outline-none transition-colors placeholder:text-outline/50 ${
                emailError ? 'border-error' : 'border-outline focus:border-secondary'
              }`}
              placeholder="you@company.com"
              autoComplete="email"
            />
            {emailError && <p className="mt-1.5 text-xs text-error">{emailError}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block font-mono text-[10px] text-outline uppercase tracking-widest mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setPasswordTouched(true)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLogin() }}
                className={`w-full bg-surface-container-low border-b text-white p-3 pr-11 outline-none transition-colors placeholder:text-outline/50 ${
                  passwordError ? 'border-error' : 'border-outline focus:border-secondary'
                }`}
                placeholder="Your password"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-outline hover:text-on-surface transition-colors"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
            {passwordError && <p className="mt-1.5 text-xs text-error">{passwordError}</p>}
          </div>

          {/* Turnstile */}
          <div className="flex justify-center">
            <Turnstile
              ref={turnstileRef}
              siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY as string}
              onSuccess={setTurnstileToken}
              onError={() => setTurnstileToken('')}
              onExpire={() => setTurnstileToken('')}
              options={{ theme: 'dark' }}
            />
          </div>

          <button
            onClick={handleLogin}
            disabled={!canSubmit}
            className="w-full py-3.5 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
          >
            {loading ? <LoadingSpinner size="sm" /> : 'Sign in'}
          </button>

          <p className="text-center text-xs text-outline pb-2">
            Don't have an account?{' '}
            <a href="/signup" className="text-primary hover:underline">
              Create one
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
